/**
 * Where a long-running action reports itself.
 *
 * Phase 04 ships no screen that starts a job — that is Phase 05 — but the
 * drawer is built here, because it is the component that has to get `EXPIRED`
 * right and it should exist before there are five call sites depending on it.
 *
 * The row below is the single rendering of §04.5's table, driven by
 * `describeJob()`. In particular a job's tone comes from that function and not
 * from a component-local mapping, so "EXPIRED must not read as failure" is one
 * decision in one place rather than a rule five screens have to remember.
 */

import type { ClientJob } from '@rd/shared';
import { X } from 'lucide-react';
import type { ReactElement } from 'react';

import { Button } from '../components/Button.js';
import { Spinner } from '../components/Spinner.js';
import { jobHeadline, jobWhen } from '../features/jobs/jobDescription.js';
import { JobStatusBadge } from '../features/jobs/JobStatusBadge.js';
import { JobToasts } from '../features/jobs/useJobToasts.js';
import { useJobDrawer } from '../lib/JobDrawerContext.js';
import { useJob } from '../lib/useJob.js';

export function JobDrawer(): ReactElement | null {
  const { tracked, isOpen, close } = useJobDrawer();

  /**
   * The completion toasts are watched here rather than inside the panel, so
   * they keep firing when the drawer is closed — a `PARTIAL` that lands while
   * the operator is on another screen is exactly the case §05.8's toast exists
   * for.
   */
  const toasts = <JobToasts />;

  if (!isOpen || tracked.length === 0) return toasts;

  return (
    /**
     * The drawer genuinely floats above the app, so it takes the elevation
     * token that means exactly that. `shadow-lg` slipped past the source scan
     * — it names no palette colour — but it is the same defect as a colour
     * literal: a shadow nobody can retune from the token layer, and it left the
     * `--elevation-*` tokens as dead code. `border-line-strong` frames it for
     * the same reason a card is framed: this is a container holding real
     * information, not chrome.
     */
    <aside
      aria-label="Progress"
      className="fixed bottom-0 right-0 z-40 m-4 w-96 max-w-[calc(100vw-2rem)] overflow-hidden rounded-[var(--radius-lg)] border border-line-strong bg-surface shadow-[var(--elevation-3)]"
    >
      <div className="flex items-center justify-between gap-[var(--space-3)] border-b border-line px-[var(--space-4)] py-[var(--space-2)]">
        <h2 className="text-[length:var(--text-sm)] font-semibold tracking-[var(--tracking-tight)] text-body">
          Progress
        </h2>
        <Button variant="ghost" size="sm" icon={X} onClick={close} aria-label="Close progress panel">
          Close
        </Button>
      </div>
      {toasts}
      <ul className="max-h-80 divide-y divide-line overflow-y-auto">
        {tracked.map((ref) => (
          <li key={ref} className="p-[var(--space-4)]">
            <JobRow jobRef={ref} />
          </li>
        ))}
      </ul>
    </aside>
  );
}

export function JobRow({ jobRef }: { jobRef: string }): ReactElement {
  const { job, refresh, isRefreshing, error } = useJob(jobRef);

  if (error) {
    return <p className="text-sm text-muted">This job could not be read right now.</p>;
  }
  if (!job) {
    return <Spinner label="Loading job" />;
  }

  return <JobSummary job={job} onRecheck={() => void refresh()} rechecking={isRefreshing} />;
}

/**
 * Split out from `JobRow` so it can be rendered from a plain `ClientJob` — the
 * dashboard's recent-activity list and the tests both use it without a query.
 */
/**
 * Split out from `JobRow` so it can be rendered from a plain `ClientJob` — the
 * dashboard's recent-activity list and the tests both use it without a query.
 *
 * **Everything status-shaped goes through `JobStatusBadge`** (criterion 18).
 * This component used to map tone to a badge itself and call `describeJob()`
 * directly, which meant the `EXPIRED`-is-not-failure rule had two
 * implementations that could drift — the drawer saying one thing and the job
 * list another about the same job.
 */
export function JobSummary({
  job,
  errorCode,
  onRecheck,
  compact = false,
}: {
  job: ClientJob;
  errorCode?: string | null;
  onRecheck?: () => void;
  /** Retained for callers; the badge owns the pending state of its own control. */
  rechecking?: boolean;
  /** Dense contexts drop the detail sentence when it only repeats the pill. */
  compact?: boolean;
}): ReactElement {
  return (
    <div className="flex flex-col gap-1.5">
      {/*
        The action leads. The ref is our correlation handle, not a headline —
        five cards headed by five ULIDs are five cards an operator cannot tell
        apart, answering a question they did not ask.
      */}
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="text-[length:var(--text-base)] font-medium text-body">
            {jobHeadline(job)}
          </p>
          <p className="text-[length:var(--text-2xs)] text-muted">
            {jobWhen(job)}
            {' · '}
            {/*
              The **full** ref is in the DOM and only visually truncated.
              Slicing the string shortened the text content too, which broke a
              Phase 05 safety e2e that matches the ref exactly — and criterion 1
              makes a diff to that spec a PM decision, so the implementation
              moved instead. It is also simply better: the whole ref stays
              selectable and copyable for a support thread.
            */}
            <span
              className="inline-block max-w-[9rem] truncate align-bottom font-[family-name:var(--font-mono)]"
              title={job.ref}
            >
              {job.ref}
            </span>
          </p>
        </div>
        <JobStatusBadge
          job={job}
          errorCode={errorCode ?? null}
          compact={compact}
          {...(onRecheck ? { onRecheck } : {})}
        />
      </div>
    </div>
  );
}
